import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, FormControl, Select, MenuItem, Button, Grid, Paper, Slider, TextField } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Cart from "../Cart";

// --- STYLING ---
const BakeryButton = styled(Button)({
  background: 'linear-gradient(45deg, #e88ba9 0%, #f4a2bc 100%)',
  borderRadius: '30px',
  border: 0,
  color: 'white',
  padding: '12px 32px',
  boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '1.05rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)',
    background: 'linear-gradient(45deg, #f4a2bc 0%, #e88ba9 100%)',
  },
});

const SoftFormControl = styled(FormControl)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fffafb',
    '&.Mui-focused fieldset': {
      borderColor: '#e88ba9',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#e88ba9',
  }
});

const SoftTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fffafb',
    '&.Mui-focused fieldset': {
      borderColor: '#e88ba9',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#e88ba9',
  }
});

// Color mapping for flavors
const layerColors = {
  vanilla: '#fef1d8',
  chocolate: '#5a3d31',
  strawberry: '#ffb7c5',
  redvelvet: '#8a1f2b',
};

// SVG Patterns for 2D decorations
const decorationPatterns = {
  none: null,
  sprinkles: (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', backgroundImage: 'radial-gradient(circle, #ff69b4 2px, transparent 2.5px), radial-gradient(circle, #ffd700 2px, transparent 2.5px), radial-gradient(circle, #00ced1 2px, transparent 2.5px)', backgroundSize: '15px 15px, 20px 20px, 25px 25px', backgroundPosition: '0 0, 10px 10px, 5px 15px', opacity: 0.7, borderRadius: 'inherit' }} />
  ),
  drip: (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '40%', backgroundColor: '#5a3d31', borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit', clipPath: 'polygon(0 0, 100% 0, 100% 30%, 90% 100%, 80% 40%, 70% 90%, 60% 30%, 50% 100%, 40% 40%, 30% 90%, 20% 30%, 10% 100%, 0 40%)' }} />
  ),
  frostingSwirls: (
    <div style={{ position: 'absolute', top: '-15px', left: '10%', width: '80%', height: '20px', display: 'flex', justifyContent: 'space-around' }}>
      <div style={{ width: '25px', height: '25px', borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
      <div style={{ width: '25px', height: '25px', borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
      <div style={{ width: '25px', height: '25px', borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
      <div style={{ width: '25px', height: '25px', borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
    </div>
  )
};

const CustomizeCake = () => {
  const navigate = useNavigate();
  const [layers, setLayers] = useState(2);
  const [flavor, setFlavor] = useState('vanilla');
  const [filling, setFilling] = useState('buttercream');
  const [decoration, setDecoration] = useState('none');
  const [message, setMessage] = useState('');
  const [price, setPrice] = useState(0);

  useEffect(() => {
    calculatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, flavor, filling, decoration, message]);

  const calculatePrice = () => {
    let basePrice = 2000; // Base 1 layer price
    basePrice += (layers - 1) * 800; // 800 per extra layer

    if (flavor === 'redvelvet' || flavor === 'chocolate') basePrice += 200;
    if (decoration !== 'none') basePrice += 300;
    // Wording cost
    if (message.trim().length > 0) basePrice += 200;

    setPrice(basePrice);
  };

  const handleAddToCart = () => {
    const customCake = {
      _id: `custom_${Date.now()}`,
      name: `Custom ${layers}-Layer ${flavor === 'redvelvet' ? 'Red Velvet' : flavor.charAt(0).toUpperCase() + flavor.slice(1)} Cake`,
      price: `LKR.${price}.00`,
      description: `A custom-built ${layers} layer cake. Flavor: ${flavor}, Filling: ${filling}, Decoration: ${decoration}. Wording: "${message || 'None'}"`,
      category: 'Build Your Own Cake',
      imageUrl: 'https://images.unsplash.com/photo-1464349153735-7db50ed83c84?auto=format&fit=crop&q=80&w=400'
    };

    Cart.addCake(customCake, 1);
    alert('Your custom creation has been added to the cart! 🎂');
    navigate('/menu');
  };

  // Render the Cake digitally using CSS
  const renderCake = () => {
    const cakeLayers = [];
    const cakeColor = layerColors[flavor];
    const fillingColor = filling === 'chocolate' ? '#3d2314' : filling === 'strawberry' ? '#ff6b81' : '#fff';

    for (let i = 0; i < layers; i++) {
      // Only top layer gets the main decorations
      const isTopLayer = i === layers - 1;

      cakeLayers.push(
        <Box key={`layer-${i}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Filling (goes below the layer, unless it's the bottom layer) */}
          {i > 0 && (
            <Box sx={{
              width: layers === 3 && i === 2 ? '160px' : layers === 3 && i === 1 ? '190px' : layers === 2 && i === 1 ? '190px' : '200px',
              height: '10px',
              backgroundColor: fillingColor,
              borderRadius: '5px',
              zIndex: layers - i + 1,
              my: '-2px'
            }} />
          )}

          {/* Cake Sponge */}
          <Box sx={{
            width: layers === 3 && i === 2 ? '150px' : layers === 3 && i === 1 ? '180px' : layers === 3 && i === 0 ? '210px' : layers === 2 && i === 1 ? '180px' : layers === 2 && i === 0 ? '210px' : '200px',
            height: '60px',
            backgroundColor: cakeColor,
            borderRadius: isTopLayer ? '20px 20px 4px 4px' : '4px',
            position: 'relative',
            zIndex: layers - i,
            boxShadow: 'inset -15px 0 20px rgba(0,0,0,0.1)', // 3D effect
            borderBottom: `2px solid rgba(0,0,0,0.05)`,
            transition: 'all 0.5s ease',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {/* Shadow base on the very bottom layer */}
            {i === 0 && (
              <Box sx={{ position: 'absolute', bottom: '-10px', width: '110%', height: '10px', background: 'rgba(0,0,0,0.1)', borderRadius: '50%', filter: 'blur(4px)', zIndex: -1 }} />
            )}

            {/* Apply decorations ONLY to the top layer */}
            {isTopLayer && decorationPatterns[decoration]}

            {/* Render Wording on the Top Layer */}
            {isTopLayer && message && (
              <Typography sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: filling === 'chocolate' ? '#fff' : flavor === 'chocolate' ? '#fff' : '#5c434a',
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                fontSize: message.length > 15 ? '0.8rem' : '1.1rem',
                textAlign: 'center',
                zIndex: 10,
                textShadow: '0 1px 2px rgba(255,255,255,0.5)',
                pointerEvents: 'none'
              }}>
                {message}
              </Typography>
            )}

            {/* Cake Plate on the bottom layer */}
            {i === 0 && (
              <Box sx={{ position: 'absolute', bottom: '-20px', width: '140%', height: '20px', backgroundColor: '#e2e8f0', borderRadius: '50%', borderBottom: '6px solid #cbd5e1', zIndex: -2 }} />
            )}
          </Box>
        </Box>
      );
    }

    // Reverse array so bottom layer renders first in the DOM flow (or Flex-col-reverse)
    return (
      <Box sx={{
        height: '240px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translateY(-120px)', // Moves the cake up visually
        position: 'relative'
      }}>
        {cakeLayers}
      </Box>
    );
  };

  return (
    <Box sx={{ background: '#fffafb', minHeight: '100vh', pt: '100px', pb: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/menu')} sx={{ color: '#d46a89' }}>
            Back to Menu
          </Button>
        </Box>

        <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#d46a89', fontFamily: '"Playfair Display", serif', textAlign: 'center', mb: 2 }}>
          Build Your Dream Cake
        </Typography>
        <Typography variant="body1" sx={{ color: '#8c7077', textAlign: 'center', mb: 6 }}>
          Watch your custom masterpiece come to life as you design it.
        </Typography>

        <Grid container spacing={4}>
          {/* Left Side: Controls */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #fcf4f4', background: '#fff', height: '100%' }}>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#5c434a', mb: 2, fontWeight: 700 }}>1. Choose Cake Layers</Typography>
                <Slider
                  value={layers}
                  min={1}
                  max={3}
                  step={1}
                  marks={[{ value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }]}
                  onChange={(e, val) => setLayers(val)}
                  sx={{ color: '#e88ba9', '& .MuiSlider-markLabel': { fontSize: '1.2rem', fontWeight: 600, color: '#8c7077' } }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#5c434a', mb: 2, fontWeight: 700 }}>2. Sponge Flavor</Typography>
                <Grid container spacing={2}>
                  {[
                    { id: 'vanilla', label: 'Vanilla', bg: layerColors.vanilla },
                    { id: 'chocolate', label: 'Chocolate', bg: layerColors.chocolate },
                    { id: 'strawberry', label: 'Strawberry', bg: layerColors.strawberry },
                    { id: 'redvelvet', label: 'Red Velvet', bg: layerColors.redvelvet }
                  ].map(f => (
                    <Grid item xs={6} key={f.id}>
                      <Box
                        onClick={() => setFlavor(f.id)}
                        sx={{
                          p: 2, borderRadius: '12px', border: '2px solid',
                          borderColor: flavor === f.id ? '#e88ba9' : '#fcf4f4',
                          backgroundColor: flavor === f.id ? '#fffafb' : '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                          transition: 'all 0.2s', '&:hover': { borderColor: '#e88ba9' }
                        }}
                      >
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: f.bg, border: '1px solid rgba(0,0,0,0.1)' }} />
                        <Typography sx={{ fontWeight: flavor === f.id ? 700 : 500, color: '#5c434a' }}>{f.label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#5c434a', mb: 2, fontWeight: 700 }}>3. Filling & Frosting</Typography>
                <SoftFormControl fullWidth size="small">
                  <Select value={filling} onChange={(e) => setFilling(e.target.value)}>
                    <MenuItem value="buttercream">Vanilla Buttercream</MenuItem>
                    <MenuItem value="chocolate">Chocolate Ganache</MenuItem>
                    <MenuItem value="strawberry">Strawberry Jam</MenuItem>
                  </Select>
                </SoftFormControl>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#5c434a', mb: 2, fontWeight: 700 }}>4. Toppings & Decorations</Typography>
                <SoftFormControl fullWidth size="small">
                  <Select value={decoration} onChange={(e) => setDecoration(e.target.value)}>
                    <MenuItem value="none">Smooth Finish (None)</MenuItem>
                    <MenuItem value="sprinkles">Rainbow Sprinkles</MenuItem>
                    <MenuItem value="drip">Chocolate Drip</MenuItem>
                    <MenuItem value="frostingSwirls">Frosting Swirls</MenuItem>
                  </Select>
                </SoftFormControl>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#5c434a', mb: 2, fontWeight: 700 }}>5. Icing Message (+LKR 200)</Typography>
                <SoftTextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Happy Birthday!"
                  value={message}
                  onChange={(e) => setMessage(e.target.value.substring(0, 25))} // Limit to 25 chars
                  helperText={`${message.length}/25 characters`}
                />
              </Box>

            </Paper>
          </Grid>

          {/* Right Side: Visualizer */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              p: 4, borderRadius: '24px', border: '1px solid #fcf4f4',
              background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
              height: '100%', display: 'flex', flexDirection: 'column'
            }}>
              <Typography variant="h5" sx={{ textAlign: 'center', color: '#5c434a', mb: 2, fontWeight: 'bold' }}>Live Preview</Typography>

              {/* The Digital Cake Wrapper */}
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', position: 'relative' }}>

                {/* Confetti Background effect */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.05, backgroundImage: 'radial-gradient(circle, #ff69b4 2px, transparent 2.5px)', backgroundSize: '30px 30px', zIndex: 0 }} />

                {renderCake()}

              </Box>

              <Box sx={{ mt: 4, pt: 3, borderTop: '2px dashed #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: '#8c7077', fontSize: '0.9rem', mb: 0.5 }}>Total Price</Typography>
                  <Typography variant="h4" sx={{ color: '#d46a89', fontWeight: 800 }}>LKR. {price}.00</Typography>
                </Box>
                <BakeryButton onClick={handleAddToCart}>
                  Add to Cart
                </BakeryButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>

      </Container>
    </Box>
  );
};

export default CustomizeCake;
